import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileDown, ArrowRight } from "lucide-react";
import { CERTS, DECODER_SEGMENTS } from "@/data/hvm";

const Decoder = () => {
  const [active, setActive] = useState(0);
  const seg = DECODER_SEGMENTS[active];

  return (
    <div data-testid="vsb-decoder" className="mt-16 bg-[#0E1420] border border-[#26334D] clip-notch p-6 sm:p-10">
      <p className="font-tech text-[11px] tracking-[0.3em] uppercase text-[#F4A031]">
        How to read a VSB rating code
      </p>
      <p className="text-[#94A3B8] mt-2 max-w-2xl">
        Every certified barrier carries a code that tells you exactly what it survived.
        Tap each segment of a real ISO 22343 / IWA 14-1 rating to decode it.
      </p>

      <div className="flex flex-wrap items-center gap-x-1 gap-y-3 mt-8 font-tech font-bold text-2xl sm:text-4xl">
        {DECODER_SEGMENTS.map((s, i) => (
          <span key={s.token} className="flex items-center">
            <button
              data-testid={`decoder-segment-${i}`}
              onClick={() => setActive(i)}
              className={`px-2 py-1 transition-colors ${
                active === i ? "bg-[#F4A031] text-[#0B0F17]" : "text-white hover:text-[#F4A031]"
              }`}
            >
              {s.token}
            </button>
            {i < DECODER_SEGMENTS.length - 1 && (
              <span className="text-[#384B70]">{[2, 3].includes(i) ? "/" : i === 4 ? ":" : i === 5 ? "/" : i === 0 ? "/" : "/"}</span>
            )}
          </span>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          data-testid="decoder-explanation"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.25 }}
          className="mt-8 border-l-2 border-[#F4A031] pl-5"
        >
          <p className="font-grotesk font-bold text-lg text-white">{seg.label}</p>
          <p className="text-[#94A3B8] mt-1 max-w-2xl">{seg.desc}</p>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export const Standards = () => {
  return (
    <section id="standards" data-testid="standards-section" className="relative py-24 sm:py-32 bg-[#0E1420] border-y border-[#26334D] noise-overlay">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-tech text-[11px] tracking-[0.3em] uppercase text-[#F4A031]"
        >
          Standards &amp; certification
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-4 max-w-3xl"
        >
          Four standards.<br />One language: <span className="text-[#F4A031]">proof.</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.18 }}
          className="text-[#94A3B8] mt-5 max-w-2xl"
        >
          Specifications in New Zealand and Australia cite a mix of international crash-test
          standards. Here's what each one actually certifies — and why the difference matters
          when you sign off a barrier.
        </motion.p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {CERTS.map((c, i) => (
            <motion.div
              key={c.code}
              data-testid={`cert-card-${c.code.replace(/\s+/g, "-").toLowerCase()}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: i * 0.08, duration: 0.55 }}
              className="group bg-[#131927] border border-[#26334D] hover:border-[#F4A031]/60 clip-notch p-6 transition-colors"
            >
              <div className="flex items-start justify-between">
                <p className="font-tech font-bold text-white text-lg leading-tight">{c.code}</p>
                <div className="flex flex-col items-end gap-1.5">
                  <span className="font-tech text-[10px] tracking-widest text-[#64748B] border border-[#26334D] px-2 py-0.5">
                    {c.year}
                  </span>
                  <span
                    data-testid={`cert-status-${c.code.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`font-tech text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 border ${
                      c.status === "current"
                        ? "text-[#22C55E] border-[#22C55E]/40 bg-[#22C55E]/10"
                        : "text-[#EF4444] border-[#EF4444]/40 bg-[#EF4444]/10"
                    }`}
                  >
                    {c.status === "current" ? "Current" : "Superseded"}
                  </span>
                </div>
              </div>
              <p className="font-grotesk font-semibold text-[#F4A031] text-sm mt-3">{c.title}</p>
              <p className="text-sm text-[#94A3B8] leading-relaxed mt-2">{c.body}</p>
              <p className="font-tech text-[11px] text-[#64748B] mt-4 pt-4 border-t border-[#26334D] group-hover:text-[#F4A031] transition-colors">
                {c.example}
              </p>
              <a
                href={c.guide}
                data-testid={`cert-guide-link-${c.code.replace(/\s+/g, "-").toLowerCase()}`}
                className="inline-flex items-center gap-1.5 font-tech text-[10px] tracking-[0.2em] uppercase text-[#F4A031] mt-3 hover:gap-2.5 transition-all"
              >
                Read the full guide <ArrowRight size={12} />
              </a>
            </motion.div>
          ))}
        </div>

        <motion.p
          data-testid="standards-changeover-note"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-8 border-l-2 border-[#F4A031] pl-5 text-sm text-[#94A3B8] max-w-3xl leading-relaxed"
        >
          <span className="text-white font-semibold">Standards changeover:</span> BSI PAS 68 and IWA 14-1 were
          officially replaced by ISO 22343 in September 2023. Certificates issued under the older standards remain
          valid proof of performance — the Access X guide below covers the full changeover in detail.
        </motion.p>

        <motion.a
          href="/brochures/accessx-hvm-crash-testing-standards-guide.pdf"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="standards-guide-download"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-8 inline-flex items-center gap-3 border border-[#F4A031]/40 bg-[#F4A031]/5 hover:bg-[#F4A031] hover:text-[#0B0F17] text-[#F4A031] font-tech text-xs tracking-[0.2em] uppercase px-5 py-3.5 clip-notch-sm transition-colors"
        >
          <FileDown size={15} /> Download: Access X guide to HVM crash-test standards (PDF)
        </motion.a>

        <Decoder />
      </div>
    </section>
  );
};
